/**
 * Value formatting and parsing shared by the table and the form.
 *
 * The minute-of-day pair is the interesting part. Windows are stored as
 * SMALLINT minutes because that is what the pipeline compares
 * (`start <= local.hour * 60 + local.minute < end`), and because an exclusive
 * end of midnight is 1440 — a value `TIME` cannot hold and `<input type="time">`
 * cannot display. So we parse and render HH:MM by hand and accept "24:00".
 */

/** 480 -> "08:00". 1440 -> "24:00". Null passes through. */
export function minutesToHhmm(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return ''
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

/**
 * "08:00" -> 480, "24:00" -> 1440, "8:5" -> 485. Returns null for empty input
 * and `undefined` for something unparseable, so a caller can tell "cleared"
 * from "typed nonsense" — the distinction the env-var parser never made, and
 * the reason a typo used to become a log line nobody read.
 */
export function hhmmToMinutes(value: string): number | null | undefined {
  const trimmed = value.trim()
  if (!trimmed) return null
  const match = /^(\d{1,2}):?(\d{2})$/.exec(trimmed)
  if (!match) return undefined
  const hours = Number(match[1])
  const mins = Number(match[2])
  if (mins > 59) return undefined
  const total = hours * 60 + mins
  if (total > 1440) return undefined
  return total
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const pad = (n: number) => String(n).padStart(2, '0')

/**
 * The API sends UTC with a `Z` (`2026-09-23T23:14:03Z`), so the browser can
 * show it in the viewer's own zone: an ANZ admin and a NAM admin each read
 * their local time for the same change.
 *
 * A value with no `Z` or offset is never handed to `new Date()` — the browser
 * would guess its zone. It is shown as written instead. That covers an API
 * that predates the UTC switch, and the demo fixtures.
 */
export function formatTimestamp(value: unknown): string {
  if (!value) return '—'
  const text = String(value)
  if (/(Z|[+-]\d{2}:?\d{2})$/.test(text)) {
    const at = new Date(text)
    if (!Number.isNaN(at.getTime())) {
      return `${at.getDate()} ${MONTHS[at.getMonth()]} ${at.getFullYear()}, ${pad(at.getHours())}:${pad(at.getMinutes())}`
    }
  }
  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(text)
  if (!match) return text
  const [, year, month, day, hour, minute] = match
  return `${Number(day)} ${MONTHS[Number(month) - 1]} ${year}, ${hour}:${minute}`
}

/** Short form for dense table cells. */
export function formatTimestampShort(value: unknown): string {
  const full = formatTimestamp(value)
  return full === '—' ? full : full.replace(/, \d{2}:\d{2}$/, '')
}

/** Strips the domain for a compact "changed by" column. */
export function shortenUpn(value: unknown): string {
  const text = String(value ?? '')
  if (!text) return '—'
  if (text === 'seed' || text === 'bootstrap') return text
  return text.split('@')[0]
}

/** True for the values the API treats as "no value". */
export function isBlank(value: unknown): boolean {
  return value === null || value === undefined || value === ''
}
